import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-60cdb818"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-valuenow", "aria-valuetext"]
const _hoisted_2 = ["width", "height", "viewBox"]
const _hoisted_3 = ["cx", "cy", "r", "stroke-width"]
const _hoisted_4 = ["cx", "cy", "r", "stroke-width", "stroke"]
const _hoisted_5 = ["d", "stroke-width"]
const _hoisted_6 = ["d", "stroke-width", "stroke"]
const _hoisted_7 = ["x", "y"]
const _hoisted_8 = ["x", "y"]
const _hoisted_9 = ["x", "y"]

import { computed } from "vue";

type Size = "xxs" | "xs" | "sm" | "md" | "lg" | "lgBorder";
type Shape = "circle" | "half";


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoProgressCircleComponent',
  props: {
  value: { type: Number, default: 40 },
  size: { type: String as () => Size, default: "md" as Size },
  shape: { type: String as () => Shape, default: "circle" as Shape },
  showLabel: { type: Boolean, default: true },
  caption: { type: String, default: "Active users" },
  animate: { type: Boolean, default: true },
  transitionDuration: { type: Number, default: 300 },
  transitionEasing: { type: String, default: "cubic-bezier(0.2, 0, 0, 1)" },
  color: { type: String, default: "#7453ed" },
  displayValue: { type: String, default: "" },
  showPercentSign: { type: Boolean, default: true },
  mastered: { type: Number, default: 0 },
  total: { type: Number, default: 0 },
},
  setup(__props) {

const props = __props;



const clampedValue = computed<number>(() => Math.max(0, Math.min(100, Math.round(props.value))));
const visualValue = computed<number>(() => (clampedValue.value === 0 ? 1 : clampedValue.value));
const isCircle = computed<boolean>(() => props.shape === "circle");

const sizeMapPx: Record<Size, number> = {
  xxs: 64,
  xs: 160,
  sm: 200,
  md: 240,
  lg: 280,
  lgBorder: 280
};

const strokeMap: Record<Size, number> = {
  xxs: 6,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  lgBorder: 24
};

const sizePx = computed<number>(() => sizeMapPx[props.size] ?? 240);
const stroke = computed<number>(() => strokeMap[props.size] ?? 16);
const center = computed<number>(() => sizePx.value / 2);
const radius = computed<number>(() => (sizePx.value - stroke.value) / 2);
const circumference = computed<number>(() => 2 * Math.PI * radius.value);
const halfLength = computed<number>(() => Math.PI * radius.value);

const svgHeight = computed<number>(() => {
  if (isCircle.value) return sizePx.value;
  const mapNoLabel: Record<Size, number> = { xxs: 36, xs: 88, sm: 110, md: 132, lg: 154 };
  const mapWithLabel: Record<Size, number> = { xxs: 55, xs: 88, sm: 110, md: 132, lg: 154 };
  const base = props.showLabel ? mapWithLabel[props.size] : mapNoLabel[props.size];
  return base ?? Math.round(sizePx.value * 0.55);
});

const viewBox = computed<string>(() => `0 0 ${sizePx.value} ${svgHeight.value}`);

const halfPathD = computed<string>(() => {
  const cx = center.value;
  const cy = isCircle.value ? center.value : stroke.value / 2 + radius.value;
  const r = radius.value;
  return `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
});

const progressColor = computed<string>(() => props.color);

const progressStyleCircle = computed<Record<string, string>>(() => {
  const dash = `${circumference.value.toFixed(3)} ${circumference.value.toFixed(3)}`;
  const offset = ((100 - visualValue.value) / 100) * circumference.value;
  return {
    strokeDasharray: dash,
    strokeDashoffset: offset.toFixed(3),
    transform: `rotate(-90deg)`,
    transformOrigin: `${center.value}px ${center.value}px`,
    transition: props.animate ? `stroke-dashoffset ${props.transitionDuration}ms ${props.transitionEasing}` : "none",
  };
});

const progressStyleHalf = computed<Record<string, string>>(() => {
  const dash = `${halfLength.value.toFixed(3)} ${halfLength.value.toFixed(3)}`;
  const offset = ((100 - visualValue.value) / 100) * halfLength.value;
  return {
    strokeDasharray: dash,
    strokeDashoffset: offset.toFixed(3),
    transition: props.animate ? `stroke-dashoffset ${props.transitionDuration}ms ${props.transitionEasing}` : "none",
  };
});

const centerY = computed<number>(() => (isCircle.value ? center.value : svgHeight.value / 2));

const captionYOffsetCircle = computed<number>(() => {
  const map: Record<Size, number> = { xxs: 10, xs: 24, sm: 28, md: 32, lg: 36, lgBorder: 36 };
  return map[props.size] ?? 32;
});

const percentYOffsetCircle = computed<number>(() => {
  if (!props.showLabel) return 0;
  const map: Record<Size, number> = { xxs: 6, xs: 12, sm: 8, md: 8, lg: 8, lgBorder: 8 };
  return map[props.size] ?? 8;
});

const captionYOffsetHalf = computed<number>(() => {
  const map: Record<Size, number> = { xxs: 12, xs: 8, sm: 5, md: 8, lg: 5, lgBorder: 5 };
  return map[props.size] ?? 12;
});

const percentYOffsetHalf = computed<number>(() => {
  const map: Record<Size, number> = { xxs: 26, xs: 26, sm: 32, md: 40, lg: 47, lgBorder: 47 };
  return map[props.size] ?? 32;
});

const noListAssigned = computed<boolean>(() => {
  const showPercentFalse =
    props.showPercentSign === false || props.showPercentSign === ("0" as any) || props.showPercentSign === (0 as any);
  const dv = String(props.displayValue || "");
  return showPercentFalse && dv === "0/0";
});

// Check if displayValue is a fraction (e.g., "8/10")
const isFractionDisplay = computed<boolean>(() => {
  return props.displayValue.includes('/');
});

// Parse fraction parts
const fractionParts = computed<{ numerator: string; denominator: string }>(() => {
  if (!isFractionDisplay.value) {
    return { numerator: '', denominator: '' };
  }
  const parts = props.displayValue.split('/');
  return {
    numerator: parts[0] || '0',
    denominator: '/' + (parts[1] || '0')
  };
});

// Font sizes for fraction display
const numeratorFontSize = computed<number>(() => {
  // Based on Figma: 24px for main number
  const map: Record<Size, number> = { 
    xxs: 14, 
    xs: 20, 
    sm: 24, 
    md: 28, 
    lg: 32,
    lgBorder: 32
  };
  return map[props.size] ?? 24;
});

const denominatorFontSize = computed<number>(() => {
  // Based on Figma: 16px for denominator
  const map: Record<Size, number> = { 
    xxs: 10, 
    xs: 14, 
    sm: 16, 
    md: 18, 
    lg: 20,
    lgBorder: 20
  };
  return map[props.size] ?? 16;
});

// Baseline shift to align denominator with numerator baseline
const baselineShiftValue = computed<string>(() => {
  // Shift denominator down to align with numerator baseline
  const map: Record<Size, string> = { 
    xxs: '0px', 
    xs: '-1px', 
    sm: '-2px', 
    md: '-3px', 
    lg: '-4px',
    lgBorder: '-4px',
  };
  return map[props.size] ?? '4px';
});

const percentFontSize = computed<number>(() => {
  if (noListAssigned.value) return 13;
  const map: Record<Size, number> = { xxs: 14, xs: 24, sm: 30, md: 36, lg: 48, lgBorder: 48 };
  return map[props.size] ?? 36;
});

const captionFontSize = computed<number>(() => {
  const map: Record<Size, number> = { xxs: 12, xs: 12, sm: 12, md: 14, lg: 14, lgBorder: 14 };
  return map[props.size] ?? 14;
});

const displayText = computed<string>(() => {
  if (props.displayValue) {
    return props.displayValue;
  }
  return props.showPercentSign ? `${clampedValue.value}%` : String(clampedValue.value);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "lpc",
    style: _normalizeStyle({ width: sizePx.value + 'px', height: svgHeight.value + 'px' }),
    role: "progressbar",
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    "aria-valuenow": clampedValue.value,
    "aria-valuetext": displayText.value
  }, [
    (_openBlock(), _createElementBlock("svg", {
      width: sizePx.value,
      height: svgHeight.value,
      viewBox: viewBox.value,
      fill: "none",
      "aria-hidden": "true"
    }, [
      (isCircle.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("circle", {
              class: "lpc-track",
              cx: center.value,
              cy: center.value,
              r: radius.value,
              "stroke-width": stroke.value
            }, null, 8, _hoisted_3),
            _createElementVNode("circle", {
              class: "lpc-progress",
              cx: center.value,
              cy: center.value,
              r: radius.value,
              "stroke-width": stroke.value,
              stroke: progressColor.value,
              style: _normalizeStyle(progressStyleCircle.value)
            }, null, 12, _hoisted_4)
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("path", {
              class: "lpc-track",
              d: halfPathD.value,
              "stroke-width": stroke.value,
              fill: "none"
            }, null, 8, _hoisted_5),
            _createElementVNode("path", {
              class: "lpc-progress",
              d: halfPathD.value,
              "stroke-width": stroke.value,
              fill: "none",
              stroke: progressColor.value,
              style: _normalizeStyle(progressStyleHalf.value)
            }, null, 12, _hoisted_6)
          ], 64)),
      (__props.showLabel)
        ? (_openBlock(), _createElementBlock("text", {
            key: 2,
            class: "lpc-caption",
            x: center.value,
            y: centerY.value + (isCircle.value ? -captionYOffsetCircle.value : captionYOffsetHalf.value),
            "text-anchor": "middle",
            "dominant-baseline": "central",
            style: _normalizeStyle({ fontSize: captionFontSize.value + 'px' })
          }, _toDisplayString(__props.caption), 13, _hoisted_7))
        : _createCommentVNode("", true),
      (isFractionDisplay.value)
        ? (_openBlock(), _createElementBlock("text", {
            key: 3,
            x: center.value,
            y: centerY.value + (isCircle.value ? percentYOffsetCircle.value : percentYOffsetHalf.value),
            "text-anchor": "middle",
            "dominant-baseline": "central"
          }, [
            _createElementVNode("tspan", {
              class: "lpc-numerator",
              style: _normalizeStyle({ fontSize: numeratorFontSize.value + 'px' })
            }, _toDisplayString(fractionParts.value.numerator), 5),
            _createElementVNode("tspan", {
              class: "lpc-denominator",
              style: _normalizeStyle({ 
              fontSize: denominatorFontSize.value + 'px',
              baselineShift: baselineShiftValue.value
            })
            }, _toDisplayString(fractionParts.value.denominator), 5)
          ], 8, _hoisted_8))
        : (_openBlock(), _createElementBlock("text", {
            key: 4,
            class: "lpc-percent",
            x: center.value,
            y: centerY.value + (isCircle.value ? percentYOffsetCircle.value : percentYOffsetHalf.value),
            "text-anchor": "middle",
            "dominant-baseline": "central",
            style: _normalizeStyle({ fontSize: percentFontSize.value + 'px' })
          }, _toDisplayString(displayText.value), 13, _hoisted_9))
    ], 8, _hoisted_2))
  ], 12, _hoisted_1))
}
}

})